import GeoscapeDeveloperSvg from "icons/hub-logo-signIn.svg?react";
import AustraliaSvg from "icons/australia.svg?react";
import {
  BadgeCheck,
  Clock,
  CodeXml,
  CreditCard,
  Grid2x2Check,
  Layers,
  Map,
  MapPinned,
} from "lucide-react";
import { ReactElement, useEffect, useState } from "react";
import { getRedirectInfo } from "utils/redirect-manager";
import { useTheme } from "@/components/theme-provider";

interface Point {
  title: string;
  subtitle?: string;
  icon: ReactElement;
}

interface MarketingContent {
  heading: string;
  subheading?: string;
  points: Point[];
}

export const MarketingPage = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const campaignContentMap: Record<string, MarketingContent> = {
    "env-modelling": {
      heading:
        "Create a free Geoscape Hub account to select a location and unlock insights.",
      subheading:
        "No credit card is needed - sign up today and start exploring the data.",
      points: [
        {
          title: "National Coverage",
          subtitle:
            "Authoritative, up-to-date data on buildings, land parcels and planning.",
          icon: <AustraliaSvg stroke={isDark ? "#90CAF9" : "black"} />,
        },
        {
          title: "Save Time",
          subtitle:
            "Clip what you need instantly instead of digitising or chasing open data.",
          icon: <Clock size="32px" />,
        },
        {
          title: "Self Service",
          subtitle:
            "Define sites, assign costs to clients or cost centres, and control spend.",
          icon: <Grid2x2Check size="32px" />,
        },
      ],
    },
    "addr-verify": {
      heading:
        "Create a free Geoscape Hub account to select a location and unlock insights.",
      subheading:
        "No credit card is needed - sign up today and start exploring the data.",
      points: [
        {
          title: "Address Verification",
          subtitle:
            "The only verification service powered by G-NAF Live, updated daily.",
          icon: <MapPinned size="32px" />,
        },
        {
          title: "Verify and Locate",
          subtitle:
            "Real-time verification and geocoding ensure every address is correct and accurately positioned.",
          icon: <BadgeCheck size="32px" />,
        },
        {
          title: "Enrich Customer Data",
          subtitle:
            "Enhance decision-making with property intelligence through our APIs.",
          icon: <Layers size="32px" />,
        },
      ],
    },
    default: {
      heading: "Start using Hub for free. No credit card required!",
      points: [
        {
          title:
            "Visualise the entire Geoscape product catalogue in a fast and responsive map interface.",
          icon: <Map size="32px" />,
        },
        {
          title: "Explore our range of address and building APIs.",
          icon: <CodeXml size="32px" />,
        },
        {
          title: "Download our datasets of your area of interest.",
          icon: <MapPinned size="32px" />,
        },
        {
          title:
            "Your gateway to empowered, comprehensive location enablement.",
          icon: <CreditCard size="32px" />,
        },
      ],
    },
  };

  const [content, setContent] = useState<MarketingContent>(
    campaignContentMap.default,
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const utmCampaign = searchParams.get("utm_campaign");
    if (utmCampaign && campaignContentMap[utmCampaign]) {
      setContent(campaignContentMap[utmCampaign]);
    } else if (!utmCampaign) {
      const redirectInfo = getRedirectInfo();
      if (redirectInfo?.query) {
        const pendingParams = new URLSearchParams(redirectInfo.query);
        const pendingUtmCampaign = pendingParams.get("utm_campaign");
        if (pendingUtmCampaign && campaignContentMap[pendingUtmCampaign]) {
          setContent(campaignContentMap[pendingUtmCampaign]);
          return;
        }
      }
      setContent(campaignContentMap.default);
    } else {
      setContent(campaignContentMap.default);
    }
  }, [location.search]);

  return (
    <div className="w-full max-w-sm flex flex-col gap-2">
      <div className="flex justify-start">
        <GeoscapeDeveloperSvg
          fill={isDark ? "white" : "black"}
          className="w-50"
        />
      </div>
      <h3 className="text-lg font-bold">{content.heading}</h3>
      {content.subheading && (
        <p className="text-sm text-muted-foreground">{content.subheading}</p>
      )}

      <ul className="space-y-2">
        {content.points.map((point, index) => (
          <li
            key={`point-${index}`}
            className="flex items-center gap-3 px-2 py-1"
          >
            <div className="shrink-0 flex items-center text-primary">
              {point.icon}
            </div>
            <div>
              <p
                className={`text-muted-foreground ${point.subtitle ? "text-sm font-bold mb-1" : "text-xs font-medium"}`}
              >
                {point.title}
              </p>
              {point.subtitle && (
                <p className="text-xs text-muted-foreground/70 leading-relaxed">
                  {point.subtitle}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MarketingPage;
